// Pantalla de liquidaciones ("cobro por liquidación", ej. ClubCred). Un solo
// route: arma una liquidación nueva arriba (medio, fecha, pagos pendientes,
// monto transferido) y lista el historial de liquidaciones ya confirmadas
// abajo. El detalle de cada una (con la acción de revertir) vive en
// /liquidaciones/:id — ver SettlementDetailView.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePaymentMethods } from "../payment-methods/hooks";
import type { PaymentMethod } from "../payment-methods/api";
import { useCreateSettlement, useSettlements, useSettlementsPending } from "./hooks";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { Chip } from "../../components/Chip";
import { MoneyConfirmationPanel } from "../../components/MoneyConfirmationPanel";
import { MoneyInput } from "../../components/MoneyInput";
import { PageHeader } from "../../components/PageHeader";
import {
  ArrowLeftRightIcon,
  BanknoteIcon,
  CalendarIcon,
  ChevronRightIcon,
  CreditCardIcon,
  HistoryIcon,
  ReceiptIcon,
  UserIcon,
} from "../../components/icons";
import { formatARS, getArsAmountPolicy, parseArsInput } from "../../lib/money";
import { ApiError } from "../../lib/api";
import type { CreateSettlementInput } from "./api";
import { fullName, useCustomers } from "../customers/hooks";
import type { SettlementPendingPayment } from "./api";

function todayLocalISO() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

// Mismo mapeo icon -> glifo que AdminPaymentMethodsView/SaleView (no
// exportado desde ahí, se repite acá por consistencia visual).
function MethodGlyph({ method }: { method?: Pick<PaymentMethod, "icon"> }) {
  if (method?.icon === "ArrowLeftRight") return <ArrowLeftRightIcon />;
  if (method?.icon === "CreditCard") return <CreditCardIcon />;
  if (method?.icon === "UserCircle") return <UserIcon />;
  return <BanknoteIcon />;
}

function pendingKey(payment: SettlementPendingPayment) {
  return payment.sourceType === "sale_payment"
    ? `sale_payment:${payment.salePaymentId}`
    : `account_settlement:${payment.accountSettlementId}`;
}

export function SettlementsView() {
  const navigate = useNavigate();
  const methodsQ = usePaymentMethods();
  // Solo medios activos configurados para cobro por liquidación pueden
  // liquidarse: el resto ni siquiera aparece como opción acá.
  const settleableMethods = useMemo(
    () => (methodsQ.data ?? []).filter((method) => method.settlesInBatch),
    [methodsQ.data]
  );

  const [methodId, setMethodId] = useState<number | null>(null);
  // Preselección automática: con un solo medio configurado no tiene sentido
  // pedirle al operador que lo elija.
  useEffect(() => {
    if (methodId == null && settleableMethods.length === 1) {
      setMethodId(settleableMethods[0].id);
    }
  }, [methodId, settleableMethods]);

  const [settledOn, setSettledOn] = useState(todayLocalISO());
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [transferredInput, setTransferredInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = useState<{
    body: CreateSettlementInput;
    token: string;
    amountCents: number;
  } | null>(null);

  const pendingQ = useSettlementsPending(
    {
      paymentMethodId: methodId ?? undefined,
      from: filterFrom ? `${filterFrom}T00:00:00.000Z` : undefined,
      to: filterTo ? `${filterTo}T23:59:59.999Z` : undefined,
    },
    methodId != null
  );
  const pending = useMemo(() => pendingQ.data ?? [], [pendingQ.data]);
  const customersQ = useCustomers({ includeInactive: true, limit: 200 });
  const customerNameById = useMemo(
    () => new Map((customersQ.data ?? []).map((customer) => [customer.id, fullName(customer)])),
    [customersQ.data]
  );

  // Un pago tildado que deja de estar en la lista (cambió el medio o el
  // filtro de fecha) no debe seguir "seleccionado" de forma invisible.
  useEffect(() => {
    setSelected((current) => {
      const stillPending = new Set(pending.map(pendingKey));
      const next = new Set([...current].filter((id) => stillPending.has(id)));
      return next.size === current.size ? current : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const historyQ = useSettlements();
  const createSettlement = useCreateSettlement();

  const toggle = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedTotalCents = pending
    .filter((payment) => selected.has(pendingKey(payment)))
    .reduce((sum, payment) => sum + payment.amountCents, 0);
  const transferredParsed = parseArsInput(transferredInput);
  const transferredCents = transferredParsed.ok ? transferredParsed.cents : 0;
  const transferredPolicy = transferredParsed.ok ? getArsAmountPolicy(transferredCents) : null;
  const commissionCents = selectedTotalCents - transferredCents;
  const commissionPct = selectedTotalCents > 0 ? (commissionCents / selectedTotalCents) * 100 : null;
  const showCommissionPreview = selected.size > 0 && transferredParsed.ok;
  const overTransferred = showCommissionPreview && commissionCents < 0;
  const isTransferredAmountBlocked = transferredPolicy === "blocked";

  const confirm = async () => {
    setError(null);
    if (methodId == null) return setError("Elegí un medio de pago.");
    if (selected.size === 0) return setError("Tildá al menos un pago pendiente.");
    if (!settledOn) return setError("Ingresá la fecha en que entró la plata.");
    if (!transferredParsed.ok) return setError(transferredParsed.message);
    if (isTransferredAmountBlocked) return setError("El importe supera el máximo permitido");

    // Freeze the request now. If the API asks for an unusual-amount review,
    // confirmation will retry this exact body rather than reading live form
    // state that may have changed in the meantime.
    const salePaymentIds: number[] = [];
    const accountSettlementIds: number[] = [];
    for (const payment of pending) {
      if (!selected.has(pendingKey(payment))) continue;
      if (payment.sourceType === "sale_payment") salePaymentIds.push(payment.salePaymentId);
      else accountSettlementIds.push(payment.accountSettlementId);
    }
    const body: CreateSettlementInput = {
      paymentMethodId: methodId,
      settledOn,
      transferredCents,
      salePaymentIds,
      accountSettlementIds,
    };
    try {
      const created = await createSettlement.mutateAsync(body);
      navigate(`/liquidaciones/${created.id}`, { state: { justCreated: true } });
    } catch (caught) {
      const confirmation = getMoneyConfirmation(caught);
      if (confirmation) {
        setPendingConfirmation({
          body,
          token: confirmation.token,
          amountCents: confirmation.amountCents ?? transferredCents,
        });
      } else {
        setError((caught as Error).message || "No se pudo confirmar la liquidación. Volvé a intentar.");
      }
    }
  };

  const confirmUnusualAmount = async () => {
    if (!pendingConfirmation) return;
    setError(null);
    try {
      const created = await createSettlement.mutateAsync({
        ...pendingConfirmation.body,
        moneyConfirmationToken: pendingConfirmation.token,
      });
      setPendingConfirmation(null);
      navigate(`/liquidaciones/${created.id}`, { state: { justCreated: true } });
    } catch (caught) {
      const confirmation = getMoneyConfirmation(caught);
      if (confirmation) {
        setPendingConfirmation((current) => current && {
          ...current,
          token: confirmation.token,
          amountCents: confirmation.amountCents ?? current.amountCents,
        });
      } else {
        setPendingConfirmation(null);
        setError((caught as Error).message || "No se pudo confirmar la liquidación. Volvé a intentar.");
      }
    }
  };

  return (
    <main className="page page--narrow">
      <PageHeader title="Liquidaciones" backHref="/more" />
      <section className="page-section">
        <div className="section-intro">
          <div>
            <p className="eyebrow">Cobros</p>
            <h1>Liquidaciones</h1>
            <p>Agrupá los pagos de un medio que se cobra por liquidación en la transferencia que realmente llegó.</p>
          </div>
        </div>

        {!methodsQ.isLoading && settleableMethods.length === 0 && (
          <div className="empty-state">
            <ReceiptIcon width={28} height={28} />
            <strong>No hay medios por liquidación</strong>
            <span>Activá "Se cobra por liquidación" en un medio de pago para poder liquidarlo acá.</span>
          </div>
        )}

        {settleableMethods.length > 0 && (
          <Card style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
              <strong style={{ fontSize: "var(--font-size-16)" }}>Nueva liquidación</strong>

              {settleableMethods.length > 1 ? (
                <div className="settlement-method-picker">
                  {settleableMethods.map((method) => (
                    <Chip
                      key={method.id}
                      variant="filter"
                      active={methodId === method.id}
                      icon={<MethodGlyph method={method} />}
                      onClick={() => setMethodId(method.id)}
                    >
                      {method.name}
                    </Chip>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: "var(--font-size-13)" }}>
                  Medio: <strong style={{ color: "var(--text-primary)" }}>{settleableMethods[0].name}</strong>
                </p>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                <span style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-14)", fontWeight: 600 }}>
                  Fecha en que entró la plata
                </span>
                <label className="date-control">
                  <CalendarIcon />
                  <input
                    type="date"
                    aria-label="Fecha en que entró la plata"
                    value={settledOn}
                    max={todayLocalISO()}
                    onChange={(event) => setSettledOn(event.target.value)}
                  />
                </label>
              </div>

              {methodId != null && (
                pendingConfirmation ? (
                  <MoneyConfirmationPanel
                    amountCents={pendingConfirmation.amountCents}
                    actionLabel="Confirmar liquidación"
                    pending={createSettlement.isPending}
                    onConfirm={confirmUnusualAmount}
                    onCancel={() => setPendingConfirmation(null)}
                  />
                ) : <>
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                    <div className="summary-date-range">
                      <label className="date-control">
                        <span>Desde</span>
                        <input type="date" value={filterFrom} max={filterTo || undefined} onChange={(event) => setFilterFrom(event.target.value)} />
                      </label>
                      <label className="date-control">
                        <span>Hasta</span>
                        <input type="date" value={filterTo} min={filterFrom || undefined} onChange={(event) => setFilterTo(event.target.value)} />
                      </label>
                    </div>
                    {(filterFrom || filterTo) && (
                      <button type="button" className="text-action" onClick={() => { setFilterFrom(""); setFilterTo(""); }}>
                        Quitar filtro de fecha
                      </button>
                    )}
                  </div>

                  {pendingQ.isLoading && <div className="skeleton-list"><span /><span /></div>}

                  {!pendingQ.isLoading && pending.length === 0 && (
                    <div className="empty-state">
                      <ReceiptIcon width={24} height={24} />
                      <strong>Sin pagos pendientes</strong>
                      <span>No hay cobros de este medio esperando liquidación en el rango elegido.</span>
                    </div>
                  )}

                  {pending.length > 0 && (
                    <div className="entity-list">
                      {pending.map((payment) => {
                        const key = pendingKey(payment);
                        const isAccountCollection = payment.sourceType === "account_settlement";
                        const customerName = isAccountCollection
                          ? customerNameById.get(payment.customerId) ?? `Cliente #${payment.customerId}`
                          : null;
                        const occurredAt = isAccountCollection ? payment.occurredAt : payment.saleCreatedAt;
                        return (
                        <label
                          key={key}
                          className={`settlement-pending-row${selected.has(key) ? " is-checked" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(key)}
                            onChange={() => toggle(key)}
                          />
                          <span className="settlement-pending-row__body">
                            <span className={`settlement-source-tag settlement-source-tag--${isAccountCollection ? "account" : "sale"}`}>
                              {isAccountCollection ? "Cobro de cuenta" : "Venta"}
                            </span>
                            <strong>{isAccountCollection ? customerName : `Venta #${payment.saleId}`}</strong>
                            <small>
                              {new Date(occurredAt).toLocaleDateString("es-AR")}
                              {payment.sourceType === "sale_payment" && payment.isSplitSale ? " · pago parcial de una venta dividida" : ""}
                            </small>
                          </span>
                          <strong className="num">{formatARS(payment.amountCents)}</strong>
                        </label>
                        );
                      })}
                    </div>
                  )}

                  {selected.size > 0 && (
                    <div className="settlement-totals-bar">
                      <span>{selected.size} {selected.size === 1 ? "pago seleccionado" : "pagos seleccionados"}</span>
                      <strong className="num">{formatARS(selectedTotalCents)}</strong>
                    </div>
                  )}

                  <MoneyInput
                    label="Me transfirieron"
                    placeholder="$ 0"
                    value={transferredInput}
                    onChange={(event) => { setTransferredInput(event.target.value); setError(null); }}
                    error={error ?? undefined}
                  />

                  {showCommissionPreview && (
                    <div className="settlement-commission-preview">
                      <span>Comisión</span>
                      <strong className="num">
                        {formatARS(commissionCents)}
                        {commissionPct !== null ? ` (${commissionPct.toFixed(1)}%)` : ""}
                      </strong>
                    </div>
                  )}

                  {overTransferred && (
                    <div className="summary-ledger__notice">
                      La comisión daría negativa: probablemente falten pagos por tildar, o el monto transferido está mal. Igual podés confirmar si es correcto (ej. un ajuste a favor).
                    </div>
                  )}

                  <Button
                    variant="primary"
                    fullWidth
                    size="lg"
                    disabled={createSettlement.isPending || selected.size === 0 || !transferredParsed.ok || isTransferredAmountBlocked}
                    onClick={confirm}
                  >
                    {createSettlement.isPending ? "Confirmando…" : "Confirmar liquidación"}
                  </Button>
                </>
              )}
            </div>
          </Card>
        )}

        <section className="summary-group">
          <div className="summary-group__heading"><div><p className="eyebrow">Historial</p><h2>Liquidaciones confirmadas</h2></div></div>
          {historyQ.isLoading && <div className="skeleton-list"><span /><span /></div>}
          {historyQ.data?.length === 0 && (
            <div className="empty-state">
              <HistoryIcon width={24} height={24} />
              <strong>Sin liquidaciones</strong>
              <span>Todavía no se confirmó ninguna.</span>
            </div>
          )}
          {historyQ.data && historyQ.data.length > 0 && (
            <div className="entity-list">
              {historyQ.data.map((settlement) => {
                const method = methodsQ.data?.find((candidate) => candidate.id === settlement.paymentMethodId);
                return (
                  <Link
                    key={settlement.id}
                    to={`/liquidaciones/${settlement.id}`}
                    className={`entity-row${settlement.status === "reverted" ? " is-inactive" : ""}`}
                  >
                    <span className={`entity-row__icon payment-method-icon payment-${method?.color ?? "other"}`}>
                      <MethodGlyph method={method} />
                    </span>
                    <div className="entity-row__body">
                      <strong>{settlement.methodName}</strong>
                      <small>{new Date(settlement.settledOn).toLocaleDateString("es-AR")}</small>
                    </div>
                    <span className="settlement-history-amounts">
                      <strong className="num">{formatARS(settlement.transferredCents)}</strong>
                      <small>bruto {formatARS(settlement.grossCents)}</small>
                      {settlement.status === "reverted" && (
                        <span className="status-badge status-badge--danger">Revertida</span>
                      )}
                    </span>
                    <ChevronRightIcon style={{ justifySelf: "center", color: "var(--text-secondary)" }} />
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </section>
    </main>
  );
}

function getMoneyConfirmation(error: unknown): { token: string; amountCents?: number } | null {
  if (!(error instanceof ApiError) || error.status !== 422 || !error.body || typeof error.body !== "object") return null;
  const body = error.body as {
    error?: unknown;
    confirmationToken?: unknown;
    warnings?: Array<{ path?: unknown; amountCents?: unknown }>;
  };
  if (body.error !== "money_confirmation_required" || typeof body.confirmationToken !== "string") return null;

  // Prefer the transferred amount (the text the operator just entered). A
  // high gross total can still require a confirmation even when the transfer
  // itself is below the threshold, so fall back to the first server warning.
  const warning = body.warnings?.find((item) => item.path === "transferredCents") ?? body.warnings?.[0];
  return {
    token: body.confirmationToken,
    amountCents: typeof warning?.amountCents === "number" ? warning.amountCents : undefined,
  };
}
